import { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";

// Scroll reveal via IntersectionObserver — adds .in when visible
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export function Reveal({ children, className = "", delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

// Broken christmas lights scattered on a section
export function ChristmasLights({ count = 14 }) {
  const [dots] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      top: `${Math.random() * 90}%`,
      left: `${Math.random() * 96}%`,
      cls: ["xlight-r", "xlight-g", "xlight-b", "xlight-y"][i % 4],
      delay: `${Math.random() * 3}s`,
    }))
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[2]" aria-hidden="true">
      {dots.map((d, i) => (
        <span key={i} className={`xlight ${d.cls}`} style={{ top: d.top, left: d.left, animationDelay: d.delay }} />
      ))}
    </div>
  );
}

// Slow crawling worms — subtle detail
export function Worms({ count = 3 }) {
  const [worms] = useState(() =>
    Array.from({ length: count }, () => ({
      bottom: `${2 + Math.random() * 8}px`,
      left: `${Math.random() * 80}%`,
      delay: `${Math.random() * 12}s`,
    }))
  );
  return (
    <div className="absolute inset-0 pointer-events-none z-[3]" aria-hidden="true">
      {worms.map((w, i) => (
        <span key={i} className="worm" style={{ bottom: w.bottom, left: w.left, animationDelay: w.delay }} />
      ))}
    </div>
  );
}

// Flies sitting on text; on hover they scatter revealing the slogan
export function FliesSlogan({ text, className = "" }) {
  const controls = useAnimation();
  const [scattered, setScattered] = useState(false);
  const flies = useRef(
    Array.from({ length: 7 }, () => ({
      top: `${10 + Math.random() * 70}%`,
      left: `${8 + Math.random() * 84}%`,
      dx: (Math.random() - 0.5) * 400,
      dy: -120 - Math.random() * 220,
      rot: (Math.random() - 0.5) * 90,
    }))
  ).current;

  const scatter = () => setScattered(true);
  useEffect(() => {
    if (scattered) controls.start((i) => ({
      x: flies[i].dx, y: flies[i].dy, opacity: 0, rotate: flies[i].rot,
      transition: { duration: 0.9 + Math.random() * 0.5, ease: "easeOut" },
    }));
  }, [scattered, controls, flies]);

  return (
    <span
      className={`fly-wrap ${className}`}
      onMouseEnter={scatter}
      onClick={scatter}
      data-testid="flies-slogan"
    >
      <span className={`transition-opacity duration-700 ${scattered ? "opacity-100" : "opacity-90"}`}>
        {text}
      </span>
      {!scattered &&
        flies.map((f, i) => (
          <motion.span
            key={i}
            custom={i}
            animate={controls}
            className="absolute pointer-events-none select-none"
            style={{ top: f.top, left: f.left }}
            aria-hidden="true"
          >
            <Fly />
          </motion.span>
        ))}
    </span>
  );
}

function Fly() {
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" className="opacity-90">
      <ellipse cx="8" cy="7" rx="2.4" ry="3.4" fill="#0a0a0a" />
      <ellipse cx="5" cy="5" rx="3" ry="1.6" fill="#1a1a1a" opacity="0.7" transform="rotate(-25 5 5)" />
      <ellipse cx="11" cy="5" rx="3" ry="1.6" fill="#1a1a1a" opacity="0.7" transform="rotate(25 11 5)" />
      <circle cx="8" cy="3" r="1.6" fill="#101010" />
    </svg>
  );
}

// Rugged stamped 18+ marker
export function AgeStamp({ className = "" }) {
  return (
    <div
      className={`inline-flex items-center justify-center border-2 border-[#4a0404] text-[#7a1010] font-head px-3 py-1 -rotate-6 select-none ${className}`}
      style={{ boxShadow: "inset 0 0 12px rgba(74,4,4,0.5)" }}
      data-testid="age-stamp"
      aria-label="Přístupné od 18 let"
    >
      <span className="text-lg tracking-widest">18+</span>
    </div>
  );
}
